import { toActionBarBindingId } from '@sorrem/shared';
import { useCharacterHomeCharacter, useCharacterHomeItemComparisons, useCharacterHomeItemDetails } from '../../components/CharacterHomeItemTransfer';
import { CarriedSlotHotkeyLabel } from './components/CarriedSlotHotkeyLabel';
import { countOccupiedSlots } from './count-occupied-slots';
import { AsyncStateMessage, InlayPanel, ItemGrid, useItemTransferContext } from '@sorrem-client/ui/components';
import { findCarriedSlotHotkeyIndex } from '@sorrem-client/ui/shared/action-bar';
import { formatBindingLabel, useActionBarTargets, useCurrentPlayerCharacter, useInputBindings } from '@sorrem-client/ui/hooks';
import { useCallback } from 'react';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

interface InventoryMainPanelProps {
    /** The carried slot waiting for an action slot (see `useCarriedSlotHotkeyAssignment`), or null. */
    linkingSlotIndex: number | null;
    /** Starts (with a slot index) or cancels (null) picking an action slot for a carried slot. */
    onLinkingSlotChange: (slotIndex: number | null) => void;
}

/**
 * Inventory's center-panel carried items. Storage lives beside it (`InventoryStoragePanel`) - each independently reads the shared `CharacterHomeItemTransferProvider` context rather than needing to share local state.
 * Each carried slot shows the action-bar key linked to it under it; clicking that label waits for an action slot to link it to.
 */
export function InventoryMainPanel({ linkingSlotIndex, onLinkingSlotChange }: InventoryMainPanelProps): JSX.Element {
    const { character: player, isLoading, error } = useCurrentPlayerCharacter();
    const contextPlayer = useCharacterHomeCharacter();
    const itemComparisons = useCharacterHomeItemComparisons(contextPlayer);
    const renderItemDetails = useCharacterHomeItemDetails(contextPlayer);
    const { selectedItemId, clearSelectedItem, moveTo } = useItemTransferContext();
    const { targets } = useActionBarTargets();
    const { bindings } = useInputBindings();

    const handleRearrange = useCallback((event: { toIndex: number }): void => {
        moveTo('inventory', event.toIndex);
    }, [moveTo]);

    const renderHotkeyLabel = useCallback((slotIndex: number): JSX.Element => {
        const bindingId = toActionBarBindingId(findCarriedSlotHotkeyIndex(targets, slotIndex));
        const isLinking = linkingSlotIndex === slotIndex;
        return (
            <CarriedSlotHotkeyLabel
                slotIndex={slotIndex}
                keyLabel={bindingId === undefined ? '' : formatBindingLabel(bindings, bindingId)}
                isLinking={isLinking}
                onClick={() => onLinkingSlotChange(isLinking ? null : slotIndex)}
            />
        );
    }, [bindings, linkingSlotIndex, onLinkingSlotChange, targets]);

    if (contextPlayer === null) {
        return (
            <AsyncStateMessage
                isLoading={isLoading}
                error={error}
                isEmpty={player === null}
                loadingMessage="Loading inventory..."
                emptyMessage="No inventory data available."
            />
        );
    }

    return (
        <InlayPanel label={`Carried (${countOccupiedSlots(contextPlayer.carriedItems)} / ${contextPlayer.maxCarriedSlots})`} {...toTourTargetProps('carried')}>
            <ItemGrid
                totalSlots={contextPlayer.maxCarriedSlots}
                items={contextPlayer.carriedItems}
                itemComparisons={itemComparisons}
                renderItemDetails={renderItemDetails}
                container='inventory'
                size='large'
                selectedItemId={selectedItemId}
                onRearrangeItems={handleRearrange}
                onDeselectSelectedItem={clearSelectedItem}
                renderSlotLabel={renderHotkeyLabel}
            />
        </InlayPanel>
    );
}
